/*
  # News Post Modal Component
  
  1. Changes
    - Created modal for adding/editing news posts
    - Added rich text editor for content
    - Form validation and submission to database
*/

import React, { useState, useEffect } from 'react';
import { Upload, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { validateImage, convertToWebP } from '../../utils/imageUtils';
import { NewsPost } from '../../types/news';

interface NewsPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: NewsPost | null;
  onSave: () => void;
}

const NewsPostModal: React.FC<NewsPostModalProps> = ({
  isOpen,
  onClose,
  post,
  onSave
}) => {
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    image_url: '',
    published_date: new Date().toISOString().split('T')[0],
    is_published: true
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (post) {
      setFormData({
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        image_url: post.image_url,
        published_date: post.published_date,
        is_published: post.is_published
      });
    } else {
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        image_url: '',
        published_date: new Date().toISOString().split('T')[0],
        is_published: true
      });
    }
  }, [post]);

  const uploadImage = async (file: File) => {
    try {
      setUploading(true);
      setError(null);

      const validationError = validateImage(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      const webpFile = await convertToWebP(file);
      const fileName = `${Math.random()}.webp`;
      const filePath = `news/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('gallery')
        .upload(filePath, webpFile);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('gallery')
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, image_url: data.publicUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.title || !formData.excerpt || !formData.content) {
      setError('Please fill in all required fields');
      return;
    }

    if (!formData.image_url) {
      setError('Please upload an image');
      return;
    }

    try {
      const data = {
        title: formData.title,
        excerpt: formData.excerpt,
        content: formData.content,
        image_url: formData.image_url,
        published_date: formData.published_date,
        is_published: formData.is_published
      };

      if (post) {
        const { error } = await supabase
          .from('news_posts')
          .update(data)
          .eq('id', post.id);
        if (error) throw error;
      } else {
        // Get the highest display order
        const { data: existingPosts, error: orderError } = await supabase
          .from('news_posts')
          .select('display_order')
          .order('display_order', { ascending: false })
          .limit(1);

        if (orderError) throw orderError;

        const nextOrder = existingPosts && existingPosts.length > 0
          ? existingPosts[0].display_order + 1
          : 0;

        const { error } = await supabase
          .from('news_posts')
          .insert([{ ...data, display_order: nextOrder }]);
        if (error) throw error;
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving post:', err);
      setError('Failed to save post');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-[#1F2B1F] rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="text-xl font-semibold">
            {post ? 'Edit Post' : 'Add New Post'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-white/70 mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
              required
            />
          </div>

          {/* Excerpt */}
          <div>
            <label htmlFor="excerpt" className="block text-sm font-medium text-white/70 mb-2">
              Excerpt *
            </label>
            <textarea
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white resize-none"
              placeholder="Brief description of the post..."
              required
            />
          </div>

          {/* Content */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-white/70 mb-2">
              Content * (HTML supported)
            </label>
            <textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={12}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white resize-none font-mono text-sm"
              placeholder="<p>Your content here...</p>"
              required
            />
            <p className="text-xs text-white/50 mt-1">
              You can use HTML tags like &lt;p&gt;, &lt;h2&gt;, &lt;strong&gt;, etc.
            </p>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Featured Image *
            </label>
            {formData.image_url ? (
              <div className="relative aspect-video">
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, image_url: '' })}
                  className="absolute top-2 right-2 p-1 bg-red-500 rounded-full"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <label className="relative aspect-video cursor-pointer block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadImage(file);
                  }}
                  disabled={uploading}
                  className="hidden"
                />
                <div className="absolute inset-0 border-2 border-dashed border-white/20 rounded-lg flex items-center justify-center hover:border-white/40 transition-colors">
                  <div className="text-center">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-white/40" />
                    <span className="text-sm text-white/40">
                      {uploading ? 'Uploading...' : 'Click to upload image'}
                    </span>
                  </div>
                </div>
              </label>
            )}
          </div>

          {/* Published Date and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="published_date" className="block text-sm font-medium text-white/70 mb-2">
                Published Date *
              </label>
              <input
                type="date"
                id="published_date"
                value={formData.published_date}
                onChange={(e) => setFormData({ ...formData, published_date: e.target.value })}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Status
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="w-4 h-4 text-[#E85C0D] bg-white/10 border-white/20 rounded focus:ring-[#E85C0D]"
                />
                <span className="text-white">Published</span>
              </label>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-6 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : post ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsPostModal;